import type { AnchorHTMLAttributes, ButtonHTMLAttributes, ComponentType, ReactNode } from 'react';
import { formatMoney, type FormatOptions } from '../money/money';

export type Tone = 'ok' | 'warn' | 'danger' | 'draft' | 'info';

export function Pill({ tone = 'ok', children }: { tone?: Tone; children: ReactNode }) {
  return (
    <span className="el-pill" data-tone={tone}>
      {children}
    </span>
  );
}

export function Card({ as: Tag = 'div', className = '', padding, children, ...rest }: {
  as?: 'div' | 'section';
  className?: string;
  padding?: string;
  children: ReactNode;
  'aria-labelledby'?: string;
}) {
  return (
    <Tag className={`el-card ${className}`.trim()} style={padding ? { padding } : undefined} {...rest}>
      {children}
    </Tag>
  );
}

/** A titled card section with an optional link on the right. */
export function Panel({ id, title, action, flush = false, children }: {
  id: string;
  title: ReactNode;
  action?: ReactNode;
  /** Table panels run edge to edge. */
  flush?: boolean;
  children: ReactNode;
}) {
  return (
    <Card as="section" padding={flush ? '20px 0 8px' : '20px'} aria-labelledby={id}>
      <div className="el-card-head" style={flush ? undefined : { padding: '0 0 12px' }}>
        <h2 id={id} className="el-h2">{title}</h2>
        {action}
      </div>
      {children}
    </Card>
  );
}

type Variant = 'default' | 'primary' | 'danger' | 'ghost';

export function Button({ variant = 'default', size, children, type = 'button', ...rest }:
  ButtonHTMLAttributes<HTMLButtonElement> & { variant?: Variant; size?: 'sm' }) {
  return (
    <button type={type} className="el-btn" data-variant={variant} data-size={size} {...rest}>
      {children}
    </button>
  );
}

/** A link styled as a button. Pass the router's Link as `as` for client-side navigation. */
export function ButtonLink({ variant = 'default', as: As, to, children, ...rest }:
  Omit<AnchorHTMLAttributes<HTMLAnchorElement>, 'href' | 'aria-current'> & {
    variant?: Variant;
    to: string;
    as?: LinkComponent;
  }) {
  if (As) return <As to={to} className="el-btn" data-variant={variant} {...rest}>{children}</As>;
  return <a href={to} className="el-btn" data-variant={variant} {...rest}>{children}</a>;
}

export type LinkComponent = ComponentType<{
  to: string;
  className?: string;
  children?: ReactNode;
  'aria-current'?: 'page';
  [key: string]: unknown;
}>;

export function Money({ value, currency = 'EUR', tone, ...opts }: FormatOptions & {
  value: string;
  currency?: string;
  tone?: 'positive' | 'warn' | 'danger';
}) {
  const color = tone === 'positive' ? 'var(--el-brand)' : tone === 'warn' ? 'var(--el-warn-icon)' : tone === 'danger' ? 'var(--el-danger-ink)' : undefined;
  return <span className="el-num" style={color ? { color } : undefined}>{formatMoney(value, currency, opts)}</span>;
}

export function StatTile({ label, value, currency = 'EUR', note, loading = false }: {
  label: string;
  value: string | undefined;
  currency?: string;
  note?: ReactNode;
  loading?: boolean;
}) {
  return (
    <Card className="el-stat">
      <div className="el-stat-label">{label}</div>
      {loading || value === undefined ? (
        <div className="el-skeleton" style={{ height: 34, width: '60%', marginTop: 6 }} aria-label={`${label} loading`} />
      ) : (
        <div className="el-stat-value">{formatMoney(value, currency)}</div>
      )}
      {note && <div className="el-stat-note">{note}</div>}
    </Card>
  );
}

export function PageHeader({ title, subtitle, actions }: { title: string; subtitle?: ReactNode; actions?: ReactNode }) {
  return (
    <div className="el-page-head">
      <div>
        <h1 className="el-h1">{title}</h1>
        {subtitle && <p className="el-sub">{subtitle}</p>}
      </div>
      {actions && <div className="el-actions">{actions}</div>}
    </div>
  );
}

export function Banner({ tone = 'warn', children, action }: { tone?: 'warn' | 'info'; children: ReactNode; action?: ReactNode }) {
  return (
    <div className="el-banner" data-tone={tone} role="status">
      <svg width="18" height="18" viewBox="0 0 16 16" fill="none" aria-hidden="true" style={{ flex: 'none', marginTop: 2 }}>
        <path d="M8 2L14 13H2L8 2Z" stroke="currentColor" strokeWidth="1.3" />
        <path d="M8 6.5v3" stroke="currentColor" strokeWidth="1.3" strokeLinecap="round" />
        <circle cx="8" cy="11.3" r="0.7" fill="currentColor" />
      </svg>
      <div className="el-banner-body">{children}</div>
      {action}
    </div>
  );
}

export interface Column<T> {
  key: string;
  header: ReactNode;
  numeric?: boolean;
  render: (row: T) => ReactNode;
}

export function DataTable<T>({ columns, rows, rowKey, caption, empty = 'Nothing here yet.', hideHeader = false }: {
  columns: Column<T>[];
  rows: T[];
  rowKey: (row: T) => string;
  caption?: string;
  empty?: ReactNode;
  hideHeader?: boolean;
}) {
  return (
    <div className="el-table-wrap">
      <table className="el-table">
        {caption && <caption className="el-vh">{caption}</caption>}
        {!hideHeader && (
          <thead>
            <tr>
              {columns.map((c) => (
                <th key={c.key} scope="col" className={c.numeric ? 'el-num' : undefined}>{c.header}</th>
              ))}
            </tr>
          </thead>
        )}
        <tbody>
          {rows.length === 0 ? (
            <tr><td colSpan={columns.length} className="el-muted">{empty}</td></tr>
          ) : (
            rows.map((r) => (
              <tr key={rowKey(r)}>
                {columns.map((c) => (
                  <td key={c.key} className={c.numeric ? 'el-num' : undefined}>{c.render(r)}</td>
                ))}
              </tr>
            ))
          )}
        </tbody>
      </table>
    </div>
  );
}
